import { classnames } from 'tailwindcss-classnames';
import { Data } from '.generated/templates/Feature.Data.model';
import React from 'react';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';
import sanitizeHtml from 'sanitize-html';
import Head from 'next/head';

export type OrgSchemaProps = Sitecore.Override.ComponentBase & Data.Seo.Fields.OrganizationSchema;

// Splits a string of format '"value1","value2",...' into an array while preserving commas found
// within the individual values. Returns an empty array if the input string is null or empty.
const parseCollectionString = (collectionString?: string): string[] => {
  return collectionString?.match(/"([^"]*)"/g)?.map((name) => name.replace(/"/g, '').trim()) || [];
};

const OrganizationSchema = ({ fields }: OrgSchemaProps): React.JSX.Element => {
  const isExperienceEditor = useExperienceEditor();

  if (isExperienceEditor) {
    return (
      <div className={classnames('w-full', 'text-center')}>
        Click here to edit Organization Schema
      </div>
    );
  }

  const orgSchemaJson = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      nonprofitStatus: fields?.nonprofitStatus?.value,
      name: fields?.websiteName?.value,
      alternateName: [parseCollectionString(fields?.alternateNames?.value)],
      description: fields?.description?.value,
      url: fields?.url?.value,
      logo: fields?.logo?.value?.src ?? '',
      sameAs: [parseCollectionString(fields?.sameAs?.value)],
      telephone: fields?.telephone?.value,
      address: {
        '@type': 'PostalAddress',
        streetAddress: fields?.streetAddress?.value,
        addressLocality: fields?.addressLocality?.value,
        addressRegion: fields?.addressRegion?.value,
        addressCountry: fields?.addressCountry?.value,
        postalCode: fields?.postalCode?.value,
      },
    },
    {
      '@context': 'https://schema.org/',
      '@type': 'WebSite',
      name: fields?.websiteName?.value,
      url: fields?.url?.value,
      potentialAction: [
        {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: fields?.searchActionUrlTemplate?.value,
          },
          'query-input': fields?.searchActionQueryInput?.value,
        },
      ],
    },
  ];
  const orgSchemaJsonString = sanitizeHtml(JSON.stringify(orgSchemaJson));
  const id = 'nmdp-seo-orgschema';
  return (
    <Head>
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: orgSchemaJsonString }}
        type="application/ld+json"
        key={id}
        id={id}
      />
    </Head>
  );
};

export default OrganizationSchema;
